"use client";

import React, { useState, useEffect } from 'react';
import { Box, Spinner, Text } from '@chakra-ui/react';
import CardHeader from './CardHeader';
import CircularProgressChart from './CircularProgressChart';
import { metricsService, CallQualitySummary } from '../../../services/metricsService';

interface CallQualityCardProps {
  businessIdentifier?: string;
  timeRange?: string;
  value?: number;
  trend?: string;
  trendValue?: string;
  trendDirection?: 'up' | 'down';
}

const CallQualityCard: React.FC<CallQualityCardProps> = ({
  businessIdentifier,
  timeRange = '7d',
  value: propValue,
  trend: propTrend,
  trendValue: propTrendValue,
  trendDirection: propTrendDirection
}) => {
  const [qualityData, setQualityData] = useState<CallQualitySummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQualityData = async () => {
      // Always try to fetch from API first, use props as fallback
      setIsLoading(true);
      setError(null);
      try {
        const data = await metricsService.getCallQualitySummary(businessIdentifier, timeRange);
        setQualityData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load call quality data.');
        console.error('Error fetching call quality data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQualityData();
  }, [businessIdentifier, timeRange]);

  // Use API data if available, otherwise use prop values, otherwise use defaults
  // const value = qualityData ? qualityData.average_quality_score : (propValue !== undefined ? propValue : 55);
  const value = qualityData?.average_quality_score;
  
  // Fix trend logic: use API data if qualityData exists and has trend property (including 0)
  const trend = qualityData && qualityData.trend !== undefined ? 
    (qualityData.trend === 0 ? 'No change' : qualityData.trend > 0 ? 'Up by' : 'Down by') : 
    (propTrend || 'Up by');
    
  const trendValue = qualityData && qualityData.trend !== undefined ? 
    (qualityData.trend === 0 ? '0 points' : `${Math.abs(qualityData.trend)} points`) : 
    (propTrendValue || '4 points');
    
  const trendDirection: 'up' | 'down' = qualityData && qualityData.trend !== undefined ? 
    (qualityData.trend >= 0 ? 'up' : 'down') : 
    (propTrendDirection || 'up');

  // Use green for positive trend, red for negative
  const secondaryColor = trendDirection === 'up' ? '#79FFB5' : '#FF7979';

  if (isLoading) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        shadow="sm"
        p={{ base: "3", md: "4" }}
        h={{ base: "auto", md: "350px" }}
        minH={{ base: "335px", md: "auto" }}
        w={{ base: "full", md: "370px" }}
        maxW={{ base: "370px", md: "full" }}
        mx={{ base: "auto", md: "0" }}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        shadow="sm"
        p={{ base: "3", md: "4" }}
        h={{ base: "auto", md: "350px" }}
        minH={{ base: "335px", md: "auto" }}
        w={{ base: "full", md: "370px" }}
        maxW={{ base: "370px", md: "full" }}
        mx={{ base: "auto", md: "0" }}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="red.500" fontSize="sm" textAlign="center">{error}</Text>
      </Box>
    );
  }
  
  return (
    <Box
      bg="white"
      borderRadius="xl"
      shadow="sm"
      p={{ base: "3", md: "4" }}
      h={{ base: "auto", md: "350px" }}
      minH={{ base: "335px", md: "auto" }}
      w={{ base: "full", md: "370px" }}
      maxW={{ base: "370px", md: "full" }}
      mx={{ base: "auto", md: "0" }}
      position="relative"
    >
      <CardHeader 
        title="Call Quality" 
        trend={trend} 
        trendValue={trendValue} 
        trendDirection={trendDirection} 
      />
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        h="64" 
        mt={{ base: "5", md: "0" }}
      >
        <CircularProgressChart 
          value={value == null ? 0 : value} 
          suffix="/100"
          primaryColor="#7987FF" 
          secondaryColor={secondaryColor}
          trendValue={trendValue}
          trendDirection={trendDirection}
        />
      </Box>
    </Box>
  );
};

export default CallQualityCard; 